import { useState } from 'react'
import { Alert, App, Card, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { PageHeader } from '@/shared/ui/PageHeader'
import { RowActions } from '@/shared/ui/table/RowActions'
import { QuietHoursFormModal } from '@/features/notify/QuietHoursFormModal'
import { DELIVERY_CHANNELS, deleteQuietHours, fetchQuietHours, type DeliveryChannel, type QuietHoursView } from '@/features/notify/notifyApi'

interface Row {
  channel: DeliveryChannel
  current: QuietHoursView | null
}

/**
 * Sokin soatlar — tenant bo'yicha, kanal bo'yicha sozlanadigan.
 *
 * Har kanal uchun bitta qator (SMS/Telegram/Email/WhatsApp), qatoridan qat'i nazar bazada
 * yozuv bor-yo'qligidan — "qator yo'q = cheklovsiz" (`QuietHoursRepository`
 * javadoc'i) ni "bu kanal hali sozlanmagan" deb TUSHUNARLI ko'rsatish uchun.
 *
 * <h2>⚠️ QIDIRUV, FILTR, SAHIFALASH VA BO'SH HOLAT — ATAYLAB YO'Q</h2>
 * Qatorlar soni ma'lumotdan emas, {@code DELIVERY_CHANNELS}
 * ro'yxatidan keladi. Ya'ni:
 * <ul>
 *   <li>jadval hech qachon BO'SH bo'lmaydi — "ma'lumot yo'q" holati
 *       mavjud emas, bo'shlik matnini yozish o'lik kod bo'lardi;</li>
 *   <li>uchta qator ichidan qidirish va filtrlash foydasiz — boshqaruv
 *       elementi ekranda joy egallaydi, lekin hech qachon ishlatilmaydi
 *       (JADVAL-QOIDASI §2: "filtr yo'q joyga sun'iy filtr o'ylab
 *       topma");</li>
 *   <li>sahifalash ham shu sababdan yo'q. `GET /api/notify/quiet-hours`
 *       (`QuietHoursResource#list()`) baribir parametr qabul qilmaydi.</li>
 * </ul>
 *
 * <h2>⚠️ RO'YXAT XATOSI KO'RINADI</h2>
 * Ilgari ko'rinmasdi: so'rov 403/500 bilan yiqilsa uchala qator ham
 * «cheklovsiz» bo'lib chizilardi — ya'ni ekran "sokin soat sozlanmagan"
 * deb ISHONARLI yolg'on aytardi. Aslida sozlangan bo'lishi mumkin edi.
 */
export function NotifyQuietHoursPage() {
  const { t } = useTranslation()
  /*
    ⚠️ BUG-048 — `modal` kontekstdan olinadi: statik `Modal.confirm`
    `ConfigProvider` mavzusiga yetmaydi va qorong'i panel ustida OQ
    dialog chizadi (o'lchov `SettingsPage` dagi izohda).
  */
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const [editingChannel, setEditingChannel] = useState<DeliveryChannel | null>(null)
  const [error, setError] = useState<string | null>(null)

  const quietHours = useQuery({ queryKey: queryKeys.notify.quietHours, queryFn: fetchQuietHours })
  const byChannel = new Map((quietHours.data ?? []).map((q) => [q.channel, q]))
  const rows: Row[] = DELIVERY_CHANNELS.map((channel) => ({ channel, current: byChannel.get(channel) ?? null }))

  const remove = useMutation({
    mutationFn: (channel: DeliveryChannel) => deleteQuietHours(channel),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notify.quietHours })
      // ⚠️ Bu kanalda qator yo'qligi CHEKLOVSIZ degani
      // (QuietHoursRepository javadoc'i), ya'ni amal natijasi — "sokin
      // soat olib tashlandi", kanalning o'zi o'chirilgani emas.
      void message.success(t('common.toastDeleted'))
    },
    onError: (err) => setError(translateError(err, t)),
  })

  const columns: ColumnsType<Row> = [
    {
      // Uchta qator, sahifalanmaydi — raqam to'g'ridan-to'g'ri `index` dan.
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'channel',
      title: t('notify.channel'),
      width: 160,
      render: (_, row) => <Typography.Text strong>{t(`notify.channelName.${row.channel}`)}</Typography.Text>,
    },
    {
      key: 'range',
      title: t('notify.quietHours.range'),
      width: 200,
      render: (_, row) =>
        row.current ? (
          `${row.current.start.slice(0, 5)} — ${row.current.end.slice(0, 5)}`
        ) : (
          <Typography.Text type="secondary">{t('notify.quietHours.unrestricted')}</Typography.Text>
        ),
    },
    {
      key: 'enabled',
      title: t('subscribers.columns.status'),
      width: 120,
      render: (_, row) =>
        row.current ? (
          <Tag color={row.current.enabled ? 'green' : 'default'}>
            {row.current.enabled ? t('notify.quietHours.active') : t('notify.quietHours.inactive')}
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'edit',
              label: row.current ? t('tariffs.edit') : t('notify.quietHours.setUp'),
              onClick: () => setEditingChannel(row.channel),
              permission: 'notify.quiethours.write',
            },
            {
              key: 'remove',
              label: t('notify.quietHours.remove'),
              onClick: () => {
                modal.confirm({
                  title: t('notify.quietHours.removeTitle'),
                  content: t('notify.quietHours.removeConfirm', {
                    channel: t(`notify.channelName.${row.channel}`),
                  }),
                  okButtonProps: { danger: true },
                  okText: t('actions.delete'),
                  cancelText: t('common.cancel'),
                  onOk: () => remove.mutateAsync(row.channel),
                })
              },
              permission: 'notify.quiethours.write',
              danger: true,
              divider: true,
              hidden: row.current === null,
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.notifyQuietHours')} description={t('pageIntro.notifyQuietHours')} />

      {error ? (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {quietHours.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dataTable.loadFailed')}
          description={translateError(quietHours.error, t)}
        />
      ) : null}

      <Card>
        <Table<Row>
          columns={columns}
          dataSource={rows}
          rowKey={(row) => row.channel}
          loading={quietHours.isPending}
          size="middle"
          pagination={false}
        />
      </Card>

      <QuietHoursFormModal
        open={editingChannel !== null}
        channel={editingChannel ?? 'SMS'}
        current={editingChannel ? (byChannel.get(editingChannel) ?? null) : null}
        onClose={() => setEditingChannel(null)}
      />
    </>
  )
}
